import { Link, useNavigate } from "@tanstack/react-router";
import { Heart } from "lucide-react";
import { resolveImage } from "@/lib/image-map";
import { formatPrice } from "@/lib/utils";
import type { Product } from "@/lib/queries";
import { useWishlist } from "@/hooks/use-wishlist";
import { ImageZoom } from "@/components/image-zoom";

export function ProductCard({
  product,
  byline,
  creatorHandle,
  creatorName,
}: {
  product: Product;
  byline?: string;
  creatorHandle?: string | null;
  creatorName?: string | null;
}) {
  const { isSaved, toggle, isAuthenticated } = useWishlist();
  const navigate = useNavigate();
  const saved = isSaved(product.id);
  const handle = creatorHandle ?? product.creator_handle ?? null;
  const name = creatorName ?? product.creator_name ?? null;

  return (
    <div className="group relative block card-lift border border-border bg-card overflow-hidden rounded-sm">
      <Link to="/product/$id" params={{ id: product.id }} className="block">
        <div className="relative aspect-[4/5] overflow-hidden bg-muted">
          <ImageZoom
            src={resolveImage(product.image_key)}
            alt={product.title}
            loading="lazy"
            width={300}
            height={375}
            className="ken-burns h-full w-full object-cover"
            style={!product.in_stock ? { filter: "grayscale(40%)" } : undefined}
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink/30 via-transparent to-transparent opacity-0 transition-opacity duration-700 group-hover:opacity-100" />
          {!product.in_stock && (
            <>
              <div className="pointer-events-none absolute inset-0 bg-background/60" />
              <span className="absolute left-3 top-3 bg-background/90 px-2 py-1 text-[10px] uppercase tracking-widest backdrop-blur">Out of stock</span>
            </>
          )}
        </div>
        <div className="p-4">
          <div className="flex items-baseline justify-between gap-2">
            <div>
              <p className="text-[11px] uppercase tracking-widest text-muted-foreground">{product.brand}</p>
              <p className="mt-0.5 font-serif text-lg leading-snug">{product.title}</p>
              {product.description && (
                <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{product.description}</p>
              )}
              {product.rating != null && (
                <div className="mt-1 flex items-center gap-1">
                  <span className="text-xs">
                    {"★".repeat(Math.round(product.rating))}{"☆".repeat(5 - Math.round(product.rating))}
                  </span>
                  <span className="text-[10px] text-muted-foreground">({product.ratings_total ?? 0})</span>
                </div>
              )}
              {byline && <p className="mt-1 text-xs italic text-muted-foreground">{byline}</p>}
            </div>
            <p className="font-serif text-base">{formatPrice(product.price_cents, product.currency)}</p>
          </div>
        </div>
      </Link>
      {handle && (
        <div className="px-4 pb-4">
          <Link
            to="/creator/$handle"
            params={{ handle }}
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors"
          >
            <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-muted text-[8px] font-medium text-foreground">
              {(name ?? handle).charAt(0).toUpperCase()}
            </span>
            by @{handle}
          </Link>
        </div>
      )}
      <button
        type="button"
        aria-label={saved ? "Remove from wishlist" : "Save to wishlist"}
        aria-pressed={saved}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!isAuthenticated) {
            navigate({ to: "/login", search: { redirect: "/wishlist" } });
            return;
          }
          toggle(product.id);
        }}
        className="absolute right-3 top-3 inline-flex min-h-10 min-w-10 items-center justify-center rounded-full bg-background/90 p-2 text-foreground shadow-sm backdrop-blur transition-colors hover:bg-foreground hover:text-background"
      >
        <Heart className={`h-4 w-4 ${saved ? "fill-current" : ""}`} />
      </button>
    </div>
  );
}
